import { useEffect, useState } from 'react';

import { useGoogleMapsReady } from './useGoogleMapsReady';

export interface GooglePlaceSnapshot {
  name?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  utcOffsetMinutes?: number;
}

export interface UseGooglePlaceDetailsResult {
  place?: GooglePlaceSnapshot;
  isLoading: boolean;
  /** True when Google would not answer for this id (retired id, bad key). */
  isUnavailable: boolean;
}

/** Autocomplete-style references are not place ids and getDetails rejects them. */
const looksLikePlaceId = (id: string) => /^[A-Za-z0-9_-]{20,40}$/.test(id);

/**
 * What Google says about a stored place id right now, so the portal can show it
 * next to what Choco has on file.
 *
 * A place id can be retired or repointed after we stored it, which is exactly
 * the drift worth surfacing — so a failed lookup is a normal outcome here, not
 * an error to shout about.
 */
export const useGooglePlaceDetails = (
  placeId?: string | null
): UseGooglePlaceDetailsResult => {
  const readiness = useGoogleMapsReady();
  const [place, setPlace] = useState<GooglePlaceSnapshot>();
  const [isLoading, setIsLoading] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    if (!placeId || !looksLikePlaceId(placeId)) {
      setPlace(undefined);
      setIsUnavailable(false);
      return undefined;
    }
    if (readiness === 'loading') return undefined;
    if (readiness === 'unavailable' || !window.google?.maps?.places) {
      setIsUnavailable(true);
      return undefined;
    }

    let active = true;
    // Drop the previous place before fetching the new one: rendering A's name,
    // address and coordinates while B loads would also compare the buyer's new
    // coordinates against A's, inventing a drift warning.
    setPlace(undefined);
    setIsLoading(true);
    setIsUnavailable(false);

    const service = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );
    service.getDetails(
      {
        placeId,
        fields: [
          'name',
          'formatted_address',
          'geometry',
          'place_id',
          'utc_offset_minutes',
        ],
      },
      (result, status) => {
        if (!active) return;
        setIsLoading(false);
        const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        if (status !== ok || !result) {
          setIsUnavailable(true);
          return;
        }
        setPlace({
          name: result.name,
          formattedAddress: result.formatted_address,
          latitude: result.geometry?.location.lat(),
          longitude: result.geometry?.location.lng(),
          utcOffsetMinutes: result.utc_offset_minutes,
        });
      }
    );

    return () => {
      active = false;
    };
  }, [placeId, readiness]);

  return { place, isLoading, isUnavailable };
};
